import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { SITE_CONFIG } from "../../config/site";

export interface SeoProps {
  /** Page title (appended after site name). Overrides translated title. */
  title?: string;
  /** Translation key for title, e.g. "seo.about.title". Preferred over `title`. */
  titleKey?: string;
  /** Meta description. If not set, uses default from config. */
  description?: string;
  /** Translation key for description, e.g. "seo.about.description". */
  descriptionKey?: string;
  /** Path for canonical and OG url (e.g. "/" or "/about"). No trailing slash. */
  path?: string;
  /** Override OG/Twitter image URL (absolute). */
  image?: string;
  /** Don't add JSON-LD. */
  noJsonLd?: boolean;
  /** Meta robots (default: index, follow). */
  robots?: string;
  /** og:type — defaults to "website". Use "article" for blog posts. */
  ogType?: "website" | "article" | "profile";
  /** Extra JSON-LD graph nodes (e.g. BreadcrumbList, ItemList). */
  extraJsonLd?: Record<string, unknown>[];
}

function absoluteUrl(path: string): string {
  const base = SITE_CONFIG.url.replace(/\/$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${base}${p}`;
}

export function Seo({
  title,
  titleKey,
  description,
  descriptionKey,
  path = "/",
  image,
  noJsonLd = false,
  robots = "index, follow",
  ogType = "website",
  extraJsonLd,
}: SeoProps): React.ReactElement {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "bg";
  const canonical = absoluteUrl(path);
  const ogImage = image
    ? image.startsWith("http") ? image : absoluteUrl(image)
    : absoluteUrl(SITE_CONFIG.ogImage);

  const resolvedTitle =
    title ?? (titleKey && i18n.exists(titleKey) ? t(titleKey) : undefined);
  const metaTitle = resolvedTitle
    ? `${resolvedTitle} | ${SITE_CONFIG.shortName}`
    : SITE_CONFIG.defaultTitle;

  const resolvedDescription =
    description ??
    (descriptionKey && i18n.exists(descriptionKey) ? t(descriptionKey) : undefined);
  const metaDescription =
    resolvedDescription ||
    (lang === "en" ? SITE_CONFIG.defaultDescriptionEn : SITE_CONFIG.defaultDescription);

  const keywords = i18n.exists("seo.keywords") ? t("seo.keywords") : "";

  const orgId = `${SITE_CONFIG.url}#organization`;
  const siteId = `${SITE_CONFIG.url}#website`;
  const localBusinessId = `${SITE_CONFIG.url}#localbusiness`;

  const graph: Record<string, unknown>[] = [
    {
      "@type": "Organization",
      "@id": orgId,
      name: SITE_CONFIG.name,
      legalName: SITE_CONFIG.name,
      url: SITE_CONFIG.url,
      logo: {
        "@type": "ImageObject",
        url: absoluteUrl("/images/logo.png"),
      },
      foundingDate: SITE_CONFIG.foundingDate,
      address: {
        "@type": "PostalAddress",
        streetAddress: SITE_CONFIG.address.streetAddress,
        addressLocality: SITE_CONFIG.address.addressLocality,
        postalCode: SITE_CONFIG.address.postalCode,
        addressRegion: SITE_CONFIG.address.addressRegion,
        addressCountry: SITE_CONFIG.address.addressCountry,
      },
      contactPoint: [
        {
          "@type": "ContactPoint",
          telephone: SITE_CONFIG.phone,
          email: SITE_CONFIG.email,
          contactType: "customer service",
          availableLanguage: ["Bulgarian", "English"],
          areaServed: "BG",
        },
      ],
      ...(SITE_CONFIG.sameAs.length > 0 ? { sameAs: SITE_CONFIG.sameAs } : {}),
    },
    {
      "@type": ["LocalBusiness", "ProfessionalService", "AccountingService"],
      "@id": localBusinessId,
      name: SITE_CONFIG.name,
      image: absoluteUrl("/images/logo.png"),
      url: SITE_CONFIG.url,
      telephone: SITE_CONFIG.phone,
      email: SITE_CONFIG.email,
      priceRange: "$$",
      address: {
        "@type": "PostalAddress",
        streetAddress: SITE_CONFIG.address.streetAddress,
        addressLocality: SITE_CONFIG.address.addressLocality,
        postalCode: SITE_CONFIG.address.postalCode,
        addressRegion: SITE_CONFIG.address.addressRegion,
        addressCountry: SITE_CONFIG.address.addressCountry,
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: SITE_CONFIG.geo.latitude,
        longitude: SITE_CONFIG.geo.longitude,
      },
      openingHoursSpecification: SITE_CONFIG.openingHours.map((h) => ({
        "@type": "OpeningHoursSpecification",
        dayOfWeek: h.dayOfWeek,
        opens: h.opens,
        closes: h.closes,
      })),
      areaServed: { "@type": "Country", name: "Bulgaria" },
      parentOrganization: { "@id": orgId },
    },
    {
      "@type": "WebSite",
      "@id": siteId,
      url: SITE_CONFIG.url,
      name: SITE_CONFIG.name,
      description: metaDescription,
      publisher: { "@id": orgId },
      inLanguage: lang === "bg" ? "bg-BG" : "en-US",
    },
    {
      "@type": "WebPage",
      "@id": `${canonical}#webpage`,
      url: canonical,
      name: metaTitle,
      description: metaDescription,
      isPartOf: { "@id": siteId },
      about: { "@id": orgId },
      inLanguage: lang === "bg" ? "bg-BG" : "en-US",
    },
    ...(extraJsonLd ?? []),
  ];

  const jsonLd = !noJsonLd
    ? { "@context": "https://schema.org", "@graph": graph }
    : null;

  return (
    <Helmet htmlAttributes={{ lang: lang === "bg" ? "bg" : "en" }}>
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={robots} />
      <meta name="author" content={SITE_CONFIG.name} />
      <meta httpEquiv="content-language" content={lang === "bg" ? "bg-BG" : "en-US"} />

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={SITE_CONFIG.name} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_CONFIG.name} />
      <meta property="og:locale" content={lang === "bg" ? "bg_BG" : "en_US"} />
      <meta property="og:locale:alternate" content={lang === "bg" ? "en_US" : "bg_BG"} />

      {/* Twitter Card */}
      <meta name="twitter:card" content={SITE_CONFIG.twitterCard} />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={SITE_CONFIG.name} />

      {/* Both language variants point to the same URL (client-side i18n). */}
      <link rel="alternate" hrefLang="bg" href={canonical} />
      <link rel="alternate" hrefLang="en" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

export default Seo;
